import { Grid, useMediaQuery, useTheme } from "@mui/material";
import { useState, type FC, useMemo } from "react";
import {
  MAGIC_IMG_EXAMPLES,
  MAGIC_TYPE_DISPLAY_ENUM,
  MAGIC_TYPE_ENUM,
  MAIN_MAGIC_SHORTCUTS,
  MAIN_TEACHING_EXAMPLES,
} from "../utils/constants";
import { PreviewModal } from "./modals/PreviewModal";
import { Instructions } from "./main-page/Instructions";
import { MainMagicShortcutType, Shortcuts } from "./main-page/Shortcuts";
import { WeChatQRCode } from "./main-page/WeChatQRCode";
import { ContactQRCode } from "./main-page/ContactQRCode";
import { MainFunctions } from "./main-page/MainFunctions";

export type MainBannerItemType = {
  image: string;
  video: string;
};

export type MainMagicPromptItemType = {
  name: string;
  prompt: string;
};

type MainMagicShowcaseType = {
  original: string;
  result: string;
};

type MainMagicObjItemType = {
  prompts: MainMagicPromptItemType[];
  showcase: MainMagicShowcaseType[];
};

export type MainMagicObjType = {
  i2v: MainMagicObjItemType;
  partial: MainMagicObjItemType;
  powerful: MainMagicObjItemType;
};

// -------------------------------------------------------------------------------------------------------
type MainPageProps = {
  onMagicGeneratorClick: (type: MAGIC_TYPE_ENUM) => void;
  bannerList: MainBannerItemType[];
  magicObj: MainMagicObjType | null;
  shortcuts: MainMagicShortcutType[];
  onMagicShortcutStartClick: () => void;
  showPromptMsg: (msg: string) => void;
  onMagicShortcutModalOpenChange: (
    shortcut: MainMagicShortcutType | null
  ) => void;
};

export const MainPage: FC<MainPageProps> = ({
  onMagicGeneratorClick,
  bannerList,
  magicObj,
  shortcuts,
  onMagicShortcutStartClick,
  onMagicShortcutModalOpenChange,
}) => {
  // responsive UI
  const theme = useTheme();
  const notPhone = useMediaQuery(theme.breakpoints.up("sm"));

  const [imgPreviewModalOpen, setImgPreviewModalOpen] = useState(false);
  const [originFilePath, setOriginFilePath] = useState("");
  const [magicFilePath, setMagicFilePath] = useState("");
  const [previewType, setPreviewType] = useState(MAGIC_TYPE_ENUM.partial);

  const instructionList =
    bannerList.length > 0 ? bannerList : MAIN_TEACHING_EXAMPLES;

  const shortcutList = shortcuts.length > 0 ? shortcuts : MAIN_MAGIC_SHORTCUTS;

  const magicImgExamples = useMemo(() => {
    if (magicObj) {
      const examples = [
        {
          btnLabel: MAGIC_TYPE_DISPLAY_ENUM.partial,
          fileURIs: [] as string[][],
          disabled: false,
          type: MAGIC_TYPE_ENUM.partial,
        },
        {
          btnLabel: MAGIC_TYPE_DISPLAY_ENUM.video,
          fileURIs: [] as string[][],
          disabled: true,
          type: MAGIC_TYPE_ENUM.video,
        },
        {
          btnLabel: MAGIC_TYPE_DISPLAY_ENUM.powerful,
          fileURIs: [] as string[][],
          disabled: false,
          type: MAGIC_TYPE_ENUM.powerful,
        },
      ];

      examples.forEach((exp) => {
        if (exp.btnLabel === MAGIC_TYPE_DISPLAY_ENUM.video) {
          exp.fileURIs = magicObj.i2v.showcase.map((sc) => [
            sc.original,
            sc.result,
          ]);
        }

        if (exp.btnLabel === MAGIC_TYPE_DISPLAY_ENUM.powerful) {
          exp.fileURIs = magicObj.powerful.showcase.map((sc) => [
            sc.original,
            sc.result,
          ]);
        }

        if (exp.btnLabel === MAGIC_TYPE_DISPLAY_ENUM.partial) {
          exp.fileURIs = magicObj.partial.showcase.map((sc) => [
            sc.original,
            sc.result,
          ]);
        }
      });

      return examples;
    }

    return MAGIC_IMG_EXAMPLES;
  }, [magicObj]);

  const handleInstructionImgClick = (curImgIdx: number) => {
    setImgPreviewModalOpen(true);
    setMagicFilePath(instructionList[curImgIdx].video);
    setOriginFilePath(instructionList[curImgIdx].image);
    setPreviewType(MAGIC_TYPE_ENUM.video);
  };

  return (
    <>
      <Grid container id="main-page" spacing={notPhone ? 0 : 2}>
        {/* 演示视频滚动图列表 */}
        {instructionList.length > 0 && (
          <Instructions
            list={instructionList}
            onImgClick={handleInstructionImgClick}
          />
        )}
        <Grid size={{ xs: 12, sm: 8 }}></Grid>

        {/* 快捷功能展示列表 */}
        <Grid size={{ xs: 12, sm: 4 }}></Grid>
        <Shortcuts
          list={shortcutList}
          onMagicShortcutStartClick={onMagicShortcutStartClick}
          onMagicShortcutModalOpenChange={onMagicShortcutModalOpenChange}
        />

        {/* 魔法图展示列表 - 精准修图、万能修图、图生视频 */}
        <MainFunctions
          examples={magicImgExamples}
          onBtnClick={onMagicGeneratorClick}
        />
        <Grid size={{ xs: 12, sm: 4 }}></Grid>

        {/* 天堂相册 小程序二维码 */}
        <Grid size={{ xs: 12, sm: 8 }}></Grid>
        <WeChatQRCode />

        {/* 联系二维码 */}
        <Grid size={{ xs: 12, sm: 4 }}></Grid>
        <ContactQRCode />
      </Grid>

      <PreviewModal
        open={imgPreviewModalOpen}
        originFileUrl={originFilePath}
        magicFileUrl={magicFilePath}
        onModalClose={() => setImgPreviewModalOpen(false)}
        type={previewType}
      />
    </>
  );
};
